import { ActionCreatorWithPayload, Dispatch } from '@reduxjs/toolkit';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Language } from '../../../common/CountryMarketConfig';
import {
    CRM_DROPDOWN_MAX_OPTIONS,
    CRM_OTHER_DETAILS_MAX_LENGTH,
} from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InputGroup } from '../../../components/InputGroup';
import { Textarea } from '../../../components/Textarea';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import { getAvailableTaxonomiesFromState } from '../../../controllers/TaxonomyController/TaxonomyHelper';
import { useTaxonomyState } from '../../../controllers/TaxonomyController/TaxonomyState';
import {
    DropdownOption,
    DropdownOptionsWithUndefined,
} from '../../../lib/Dropdown/types';
import { getTaxonomyByPreferredLocale } from '../../../lib/briefing';
import { noOp } from '../../../lib/utils';
import { StepProps, TaxonomyWithKey } from '../../../types';
import { useBriefingState } from '../BriefingState';

export const onDropDownChange = (
  dispatch: Dispatch,
  selectedOption: DropdownOptionsWithUndefined,
  setAttribute: ActionCreatorWithPayload<DropdownOption[] | null>,
  setAttributeOther: ActionCreatorWithPayload<string | null>,
): void => {
  dispatch(setAttribute(selectedOption as DropdownOption[]));
  if (
    !selectedOption ||
    (Array.isArray(selectedOption) &&
      !selectedOption?.map((option) => option.value).includes('other'))
  ) {
    dispatch(setAttributeOther(null));
  }
};

export const onOtherChange = (
  dispatch: Dispatch,
  event: ChangeEvent,
  setAttributeOther: ActionCreatorWithPayload<string | null>,
): void => {
  dispatch(setAttributeOther((event.target as HTMLInputElement).value));
};

export const StepRestrictions: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const { t, i18n } = useTranslation();
  const [
    {
      restrictionsMust,
      restrictionsMustOther,
      restrictionsCan,
      restrictionsCanOther,
      requirementsMust,
      requirementsMustOther,
      requirementsCan,
      requirementsCanOther,
    },
    {
      setRestrictionsMust,
      setRestrictionsMustOther,
      setRestrictionsCan,
      setRestrictionsCanOther,
      setRequirementsMust,
      setRequirementsMustOther,
      setRequirementsCan,
      setRequirementsCanOther,
    },
  ] = useBriefingState();
  const dispatch = useDispatch();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const [taxonomyState] = useTaxonomyState();

  const availableTaxonomies = useMemo(
    () =>
      getAvailableTaxonomiesFromState(
        taxonomyState,
        countryMarketConfig,
        i18n.language as Language,
      ),
    [countryMarketConfig, i18n.language, taxonomyState],
  );

  const [isRequirementsMustValid, setIsRequirementsMustValid] = useState(true);
  const [isRequirementsCanValid, setIsRequirementsCanValid] = useState(true);
  const [isRestrictionsMustValid, setIsRestrictionsMustValid] = useState(true);
  const [isRestrictionsCanValid, setIsRestrictionsCanValid] = useState(true);
  const isStepValid = useMemo(
    () =>
      isRequirementsMustValid &&
      isRequirementsCanValid &&
      isRestrictionsMustValid &&
      isRestrictionsCanValid,
    [
      isRequirementsCanValid,
      isRequirementsMustValid,
      isRestrictionsCanValid,
      isRestrictionsMustValid,
    ],
  );
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const requirementsOptions = useMemo(() => {
    const requirementsDropdownItems: DropdownOption[] = [];
    if (Object.keys(availableTaxonomies).length) {
      Object.keys(availableTaxonomies)
        .map((key: string): TaxonomyWithKey => {
          return {
            ...getTaxonomyByPreferredLocale(
              key,
              availableTaxonomies,
              i18n.language,
            ),
            key,
          };
        })
        .filter(
          (taxonomyWithKey) =>
            !!taxonomyWithKey.data && !!taxonomyWithKey.locale,
        )
        .filter((taxonomy: TaxonomyWithKey) =>
          taxonomy.key.match(/^C_PC_[0-9]+$/g),
        )
        .forEach((requirement: TaxonomyWithKey) => {
          requirementsDropdownItems.push({
            name: requirement.data,
            value: requirement.key,
          });
        });
    }
    return requirementsDropdownItems;
  }, [availableTaxonomies, i18n.language]);

  const requirementsMustOptions = useMemo(() => {
    const options = requirementsOptions.filter(
      (option) =>
        !requirementsCan?.some(
          (element: DropdownOption) => element.value === option.value,
        ),
    );
    options.push({
      name: t('labels:other'),
      value: 'other',
    });
    return options;
  }, [requirementsCan, requirementsOptions, t]);

  const requirementsCanOptions = useMemo(() => {
    const options = requirementsOptions.filter(
      (option) =>
        !requirementsMust?.some(
          (element: DropdownOption) => element.value === option.value,
        ),
    );
    options.push({
      name: t('labels:other'),
      value: 'other',
    });
    return options;
  }, [requirementsMust, requirementsOptions, t]);

  const restrictionOptions = useMemo(
    () =>
      Object.entries(
        t('topics:restrictions:options', { returnObjects: true }),
      ).map(([value, name]) => ({ name, value } as DropdownOption)),
    [t],
  );

  const restrictionsMustOptions = useMemo(
    () =>
      restrictionOptions.filter(
        ({ value }) =>
          !restrictionsCan?.some(
            (element: DropdownOption) => element.value === value,
          ) || value === 'other',
      ),
    [restrictionsCan, restrictionOptions],
  );

  const restrictionsCanOptions = useMemo(
    () =>
      restrictionOptions.filter(
        ({ value }) =>
          !restrictionsMust?.some(
            (element: DropdownOption) => element.value === value,
          ) || value === 'other',
      ),
    [restrictionsMust, restrictionOptions],
  );

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:restrictions:heading:general`)}
        optionalField={mandatoryFields['restrictions']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <TemplateFlexGridContainer.BriefingStepSection>
          <Heading
            level="h3"
            margin={0}
            text={t('topics:restrictions:requirements')}
          />
          <div className="grid md:grid-cols-2 gap-8">
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="w-full">
                <InputGroup
                  label={t('topics:restrictions:mustComply')}
                  errorMessage={
                    requirementsOptions
                      ? ''
                      : `${t(
                          'topics:formulation-attributes:errors:productMarketingClaims',
                        )}`
                  }
                >
                  <Dropdown
                    initialSelection={requirementsMust || []}
                    options={requirementsMustOptions || []}
                    error={!requirementsMustOptions}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(selectedOption) => {
                      onDropDownChange(
                        dispatch,
                        selectedOption,
                        setRequirementsMust,
                        setRequirementsMustOther,
                      );
                    }}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>
              <div className="w-full">
                {(requirementsMust || []).some(
                  (option) => option && option.value === 'other',
                ) && (
                  <InputGroup
                    label={t('topics:restrictions:specifyRequirements')}
                  >
                    <div className="w-full __specification__ h-32">
                      <Textarea
                        placeholder={t('placeholders:textareaDefault')}
                        initialValue={requirementsMustOther || undefined}
                        onChange={(e: ChangeEvent) =>
                          onOtherChange(dispatch, e, setRequirementsMustOther)
                        }
                        onValidate={setIsRequirementsMustValid}
                        maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                      />
                    </div>
                  </InputGroup>
                )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="w-full">
                <InputGroup
                  label={t('topics:restrictions:canComply')}
                  errorMessage={
                    requirementsOptions
                      ? ''
                      : `${t(
                          'topics:formulation-attributes:errors:productMarketingClaims',
                        )}`
                  }
                >
                  <Dropdown
                    initialSelection={requirementsCan || []}
                    options={requirementsCanOptions || []}
                    error={!requirementsCanOptions}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(selectedOption) => {
                      onDropDownChange(
                        dispatch,
                        selectedOption,
                        setRequirementsCan,
                        setRequirementsCanOther,
                      );
                    }}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>
              <div className="w-full">
                {(requirementsCan || []).some(
                  (option) => option && option.value === 'other',
                ) && (
                  <InputGroup
                    label={t('topics:restrictions:specifyRequirements')}
                  >
                    <div className="w-full __specification__ h-32">
                      <Textarea
                        placeholder={t('placeholders:textareaDefault')}
                        initialValue={requirementsCanOther || undefined}
                        onChange={(e: ChangeEvent) =>
                          onOtherChange(dispatch, e, setRequirementsCanOther)
                        }
                        onValidate={setIsRequirementsCanValid}
                        maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                      />
                    </div>
                  </InputGroup>
                )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>
        <TemplateFlexGridContainer.BriefingStepSection>
          <Heading
            level="h3"
            margin={0}
            text={t('topics:restrictions:restrictions')}
          />
          <div className="grid md:grid-cols-2 gap-8">
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="w-full">
                <InputGroup label={t('topics:restrictions:mustComply')}>
                  <Dropdown
                    initialSelection={restrictionsMust || []}
                    onChange={(selectedOption) => {
                      onDropDownChange(
                        dispatch,
                        selectedOption,
                        setRestrictionsMust,
                        setRestrictionsMustOther,
                      );
                    }}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    options={restrictionsMustOptions}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>
              <div className="w-full">
                {restrictionsMust &&
                  restrictionsMust.some(
                    (option) => option && option.value === 'other',
                  ) && (
                    <InputGroup
                      label={t('topics:restrictions:specifyRestrictions')}
                    >
                      <div className="w-full __specification__ h-32">
                        <Textarea
                          placeholder={t('placeholders:textareaDefault')}
                          initialValue={restrictionsMustOther || undefined}
                          onChange={(e: ChangeEvent) =>
                            onOtherChange(dispatch, e, setRestrictionsMustOther)
                          }
                          onValidate={setIsRestrictionsMustValid}
                          maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                        />
                      </div>
                    </InputGroup>
                  )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="w-full">
                <InputGroup
                  label={t('topics:restrictions:canComply')}
                  errorMessage={
                    requirementsOptions
                      ? ''
                      : `${t(
                          'topics:formulation-attributes:errors:productMarketingClaims',
                        )}`
                  }
                >
                  <Dropdown
                    initialSelection={restrictionsCan || []}
                    options={restrictionsCanOptions}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(selectedOption) => {
                      onDropDownChange(
                        dispatch,
                        selectedOption,
                        setRestrictionsCan,
                        setRestrictionsCanOther,
                      );
                    }}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>
              <div className="w-full">
                {(restrictionsCan || []).some(
                  (option) => option && option.value === 'other',
                ) && (
                  <InputGroup
                    label={t('topics:restrictions:specifyRestrictions')}
                  >
                    <div className="w-full __specification__ h-32">
                      <Textarea
                        placeholder={t('placeholders:textareaDefault')}
                        initialValue={restrictionsCanOther || undefined}
                        onChange={(e: ChangeEvent) =>
                          onOtherChange(dispatch, e, setRestrictionsCanOther)
                        }
                        onValidate={setIsRestrictionsCanValid}
                        maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                      />
                    </div>
                  </InputGroup>
                )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
